<template>
  <aside class="flex flex-col min-h-0 h-full bg-[#11161c] border-r border-dashed border-[#2a3139]" data-testid="transcripts-sidebar">
    <div class="px-3 pt-4 pb-2">
      <div class="relative">
        <input
          v-model="searchQuery"
          type="text"
          name="search"
          class="border border-[#3a4149] rounded-[8px] bg-transparent text-white text-[13px] h-[34px] w-full pl-8 pr-2 outline-none focus:border-primary placeholder:text-white/75"
          :placeholder="translate('transcripts_search_events', 'Search events & transcripts')"
          data-testid="transcripts-sidebar-search"
        />
        <img src="@/assets/icons/search.svg" class="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2" alt="">
      </div>
    </div>

    <div class="flex-1 min-h-0 overflow-y-auto scrollbar-thin scrollbar-thumb-[#f0f6ff57] scrollbar-track-transparent px-3 pb-4">
      <div v-if="loading" class="flex flex-col gap-2 mt-4">
        <div v-for="n in 5" :key="n" class="h-8 rounded-[8px] bg-white/5 animate-pulse"></div>
      </div>

      <template v-else>
        <section v-if="permanentRooms.length" data-testid="transcripts-permanent-rooms">
          <div class="flex items-center justify-between mt-2 mb-1">
            <h3 class="text-[12px] font-semibold uppercase text-white tracking-[0.01em]">{{ translate('transcripts_permanent_rooms', 'Permanent rooms') }}</h3>
            <span class="min-w-[18px] h-[18px] px-1 rounded-full border border-white/30 text-[10px] text-white flex items-center justify-center">{{ permanentRooms.length }}</span>
          </div>
          <button
            v-for="item in permanentRooms"
            :key="item.conference_id"
            type="button"
            class="w-full flex items-center justify-between text-left text-[13px] text-white px-2 py-2 my-0.5 rounded-[6px] cursor-pointer hover:bg-[#1e2428]"
            :class="{ 'bg-[#1e2428]': item.conference_id === selectedId }"
            :data-testid="`transcripts-room-${item.conference_id}`"
            @click="$emit('select', item.conference_id)"
          >
            <span class="truncate">{{ item.title }}</span>
            <img src="@/assets/icons/arrow-right.svg" class="w-4 h-4 shrink-0" alt="">
          </button>
        </section>

        <div v-if="permanentRooms.length && singleSessions.length" class="h-px bg-white/30 my-4"></div>

        <section v-if="singleSessions.length" data-testid="transcripts-single-sessions">
          <div class="flex items-center justify-between mb-1">
            <h3 class="text-[12px] font-semibold uppercase text-white tracking-[0.01em]">{{ translate('transcripts_single_sessions', 'Single sessions / events') }}</h3>
            <span class="min-w-[18px] h-[18px] px-1 rounded-full border border-white/30 text-[10px] text-white flex items-center justify-center">{{ singleSessions.length }}</span>
          </div>
          <button
            v-for="item in singleSessions"
            :key="item.conference_id"
            type="button"
            class="w-full flex items-start gap-3 text-left px-2 py-2.5 border-b border-white/5 cursor-pointer hover:bg-[#1e2428]"
            :class="{ 'bg-[#203547] hover:bg-[#203547]': item.conference_id === selectedId }"
            :data-testid="`transcripts-session-${item.conference_id}`"
            @click="$emit('select', item.conference_id)"
          >
            <lib-icon name="files/file/outline" size="16" color="rgba(255,255,255,0.75)" class="mt-0.5 shrink-0" />
            <span class="flex flex-col min-w-0">
              <span class="text-[13px] text-white truncate">{{ item.title }}</span>
              <span class="text-[11px] text-white/55 mt-0.5">{{ formatSessionListMeta(item.timestamp, item.duration_minutes) }}</span>
            </span>
          </button>
        </section>

        <div v-if="searchQuery && !permanentRooms.length && !singleSessions.length" class="text-center text-[13px] text-white/75 mt-6">
          {{ $t('no_results_for_message') }} "{{ searchQuery }}"
        </div>
        <div v-if="!searchQuery && !transcripts.length" class="text-center text-[13px] text-white/75 mt-6 flex flex-col gap-4 items-center">
          <img src="@/assets/icons/sorenson/file.svg" alt="" class="w-8">
          <span>{{ $t('no_transcripts_message') }}</span>
        </div>
      </template>
    </div>
  </aside>
</template>

<script setup>
import { computed, defineEmits, defineProps, ref } from 'vue';
import LibIcon from '@/components/ui/LibIcon.vue';
import { translate } from '@/composables/useCaptionSettings';
import { formatSessionListMeta } from '@/utils/transcript-date';

const props = defineProps({
  transcripts: { type: Array, default: () => [] },
  selectedId: { type: String, default: null },
  loading: { type: Boolean, default: false },
});

defineEmits(['select']);

const searchQuery = ref('');

const filtered = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return props.transcripts;
  return props.transcripts.filter(item => item.title.toLowerCase().includes(query));
});

const permanentRooms = computed(() => filtered.value.filter(item => item.room_type === 'permanent'));
const singleSessions = computed(() => filtered.value.filter(item => item.room_type !== 'permanent'));
</script>
